import { useState, useEffect } from 'react'
import { Zap, RefreshCw, AlertCircle } from 'lucide-react'
import { usageService, UserUsageData, UsagePeriod } from '../../services/usageService'
import { formatUsd, formatTokens, formatInteger, formatDate, formatDayMonth } from '../../lib/formatters'

interface UserUsageSectionProps {
  userId: string
}

const PERIOD_LABELS: Record<UsagePeriod, string> = {
  today: 'Hoje',
  week: 'Semana',
  month: 'Mês',
  all: 'Total',
}

export function UserUsageSection({ userId }: UserUsageSectionProps) {
  const [data, setData] = useState<UserUsageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [period, setPeriod] = useState<UsagePeriod>('month')

  const load = async (p: UsagePeriod) => {
    try {
      setLoading(true)
      setError(null)
      const result = await usageService.getUserUsage(userId, p)
      setData(result)
    } catch (err: any) {
      console.error('Failed to load user usage:', err)
      setError('Erro ao carregar uso')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(period)
  }, [userId, period])

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Zap className="h-4 w-4 text-purple-500" />
          Consumo de IA
        </h4>
        <div className="flex items-center gap-1.5">
          {Object.entries(PERIOD_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setPeriod(key as UsagePeriod)}
              className={`px-2 py-0.5 text-xs rounded-full transition-colors ${period === key
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => load(period)}
            className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
            title="Atualizar"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-16 bg-gray-100 rounded-lg"></div>
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 text-red-500 text-sm p-3 bg-red-50 rounded-lg">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      ) : data?.totals.tokens === 0 ? (
        <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3 text-center">
          Nenhum uso registrado neste período
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-purple-50 rounded-lg p-2.5 text-center">
              <p className="text-[10px] sm:text-xs text-purple-600 font-medium">Tokens</p>
              <p className="text-sm sm:text-base font-bold text-purple-700">
                {formatTokens(data?.totals.tokens || 0)}
              </p>
            </div>
            <div className="bg-orange-50 rounded-lg p-2.5 text-center">
              <p className="text-[10px] sm:text-xs text-orange-600 font-medium flex items-center justify-center gap-0.5">
                Custo
                {data?.totals.costIsEstimated && (
                  <span className="text-[8px] bg-blue-100 text-blue-700 px-0.5 rounded">calc</span>
                )}
              </p>
              <p className="text-sm sm:text-base font-bold text-orange-700">
                {formatUsd(data?.totals.costUsd || 0)}
              </p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-2.5 text-center">
              <p className="text-[10px] sm:text-xs text-indigo-600 font-medium">Requisições</p>
              <p className="text-sm sm:text-base font-bold text-indigo-700">
                {formatInteger(data?.totals.requests || 0)}
              </p>
            </div>
          </div>

          {data?.breakdown && data.breakdown.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs sm:text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2.5 py-1.5 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Serviço</th>
                      <th className="px-2.5 py-1.5 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Modelo</th>
                      <th className="px-2.5 py-1.5 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Tokens</th>
                      <th className="px-2.5 py-1.5 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase">
                        Custo
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.breakdown.map((b, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-2.5 py-1.5 capitalize text-gray-900">{b.service.replace(/_/g, ' ')}</td>
                        <td className="px-2.5 py-1.5 font-mono text-gray-600 truncate max-w-[120px]">{b.model}</td>
                        <td className="px-2.5 py-1.5 text-right font-mono text-purple-600">{formatTokens(b.tokens)}</td>
                        <td className="px-2.5 py-1.5 text-right text-orange-600 font-mono text-xs">{formatUsd(b.costUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data?.dailyTrend && data.dailyTrend.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Tendência Diária</p>
              <div className="flex items-end gap-[2px] h-16 sm:h-20">
                {data.dailyTrend.map((d) => {
                  const maxTokens = Math.max(...data.dailyTrend.map((x) => x.tokens))
                  const height = maxTokens > 0 ? Math.max(2, (d.tokens / maxTokens) * 100) : 0
                  return (
                    <div
                      key={d.day}
                      className="flex-1 bg-purple-300 hover:bg-purple-500 rounded-sm transition-colors"
                      style={{ height: `${height}%` }}
                      title={`${formatDate(d.day)}: ${formatTokens(d.tokens)} tokens`}
                    />
                  )
                })}
              </div>
              <div className="flex justify-between mt-1 text-[10px] text-gray-400">
                <span>{formatDayMonth(data.dailyTrend[0].day)}</span>
                <span>{formatDayMonth(data.dailyTrend[data.dailyTrend.length - 1].day)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
